// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://newdietitianweb-615263253386.europe-west3.run.app/api';

/**
 * Make API request helper (adds Bearer token when available)
 */
async function apiRequest(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  try {
    const { authClient } = await import('../lib/authClient');
    const token = await authClient.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  } catch (_) { /* no token */ }

  const config = {
    method: options.method || 'GET',
    headers,
  };

  if (options.body) {
    config.body = JSON.stringify(options.body);
  }

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || error.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`❌ API Error (${endpoint}):`, error);
    throw error;
  }
}

/**
 * Profiles API
 */
export const Profiles = {
  /**
   * Get current user profile
   * @param {string} userId - UUID of the current user from auth.users
   * @returns {Promise<Object>} Profile object
   */
  async getMyProfile(userId) {
    return apiRequest(`/profiles/me?userId=${encodeURIComponent(userId)}`);
  },

  /**
   * List all profiles
   * @returns {Promise<Array>} Array of profile objects
   */
  async list() {
    return apiRequest('/profiles');
  },

  /**
   * Update a profile
   * @param {string} profileId - UUID of the profile
   * @param {Object} patch - Object containing fields to update
   * @returns {Promise<Object>} Updated profile object
   */
  async update(profileId, patch) {
    return apiRequest(`/profiles/${profileId}`, {
      method: 'PUT',
      body: patch,
    });
  },
};

/**
 * Companies API
 */
export const Companies = {
  /**
   * List all companies
   * @returns {Promise<Array>} Array of company objects
   */
  async list() {
    return apiRequest('/companies');
  },

  /**
   * One company by id. Used to label the signed-in coach's practice.
   * @param {string} id
   * @returns {Promise<Object>}
   */
  async get(id) {
    return apiRequest(`/companies/${encodeURIComponent(id)}`);
  },

  /**
   * Create a new company
   * @param {string} name - Company name
   * @returns {Promise<Object>} Created company object
   */
  async create(name) {
    return apiRequest('/companies', {
      method: 'POST',
      body: { name },
    });
  },
};

/**
 * ChatUser API (for client management)
 */
export const ChatUser = {
  /**
   * List all clients (chat_users)
   * @returns {Promise<Array>} Array of client objects
   */
  async list() {
    return apiRequest('/chat-users');
  },

  /**
   * Update a client
   * @param {string} userCode - User code of the client
   * @param {Object} patch - Object containing fields to update
   * @returns {Promise<Object>} Updated client object
   */
  async update(userCode, patch) {
    return apiRequest(`/chat-users/${userCode}`, {
      method: 'PUT',
      body: patch,
    });
  },
};

/**
 * Registration Invites API
 */
export const RegistrationInvites = {
  /**
   * List registration invites (all for sys_admin, company-scoped for company_manager when userId is passed)
   * @param {string} [userId] - Current user ID; when provided, server returns company-scoped list for company_manager
   * @returns {Promise<Array>} Array of invite objects
   */
  async list(userId) {
    const qs = userId ? `?userId=${encodeURIComponent(userId)}` : '';
    return apiRequest(`/registration-invites${qs}`);
  },

  /**
   * Create a new registration invite
   * @param {Object} inviteData - Invite data object
   * @param {string} [userId] - Current user ID; when provided as second argument, company_manager is restricted to their company
   * @returns {Promise<Object>} Created invite object
   */
  async create(inviteData, userId) {
    const qs = userId ? `?userId=${encodeURIComponent(userId)}` : '';
    return apiRequest(`/registration-invites${qs}`, {
      method: 'POST',
      body: inviteData,
    });
  },

  /**
   * Revoke a registration invite (company_manager can only revoke invites for their company when userId is passed)
   * @param {string} code - Invite code
   * @param {string} [userId] - Current user ID for authorization
   * @returns {Promise<Object>} Updated invite object
   */
  async revoke(code, userId) {
    const qs = userId ? `?userId=${encodeURIComponent(userId)}` : '';
    return apiRequest(`/registration-invites/${code}/revoke${qs}`, {
      method: 'PUT',
    });
  },
};

